import { Search, PenTool, Shield, Hammer, BarChart3, TrendingUp, GraduationCap, RefreshCw } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface Service {
  slug: string
  title: string
  description: string
  icon: LucideIcon
  capabilities: string[]
  process: string[]
  deliverables: string[]
  faqs: { question: string; answer: string }[]
}

export const SERVICES: Service[] = [
  {
    slug: 'feasibility-studies',
    title: 'Feasibility Studies',
    description: 'Rigorous, data-backed analysis informed by the latest regulations and technologies to determine project viability before you commit.',
    icon: Search,
    capabilities: [
      'Technical viability assessment',
      'Economic modelling & ROI analysis',
      'Methodology suitability review',
      'Risk identification & mitigation',
      'Preliminary carbon yield estimates',
    ],
    process: ['Scoping', 'Desk Study', 'Field Assessment', 'Feasibility Report'],
    deliverables: ['Feasibility Report', 'Financial Model', 'Risk Register', 'Methodology Recommendation'],
    faqs: [
      { question: 'How long does a feasibility study take?', answer: 'Typically 6-12 weeks depending on project complexity and access to site data.' },
      { question: 'What types of projects do you assess?', answer: 'We assess REDD+, A/R, blue carbon, biochar, BECCS, and other nature-based and engineered carbon removal approaches.' },
    ],
  },
  {
    slug: 'project-design',
    title: 'Project Design',
    description: 'Adaptive project design tailored to your ambitions and constraints, with cross-disciplinary thinking to find the best solution.',
    icon: PenTool,
    capabilities: [
      'Baseline scenario analysis',
      'Additionality demonstration',
      'Emission reduction calculations',
      'Stakeholder consultation planning',
      'Monitoring plan design',
    ],
    process: ['Scope Assessment', 'Baseline Study', 'Document Drafting', 'Review & Submission'],
    deliverables: ['Project Design Document', 'Baseline Report', 'Monitoring Plan', 'Stakeholder Consultation Report'],
    faqs: [
      { question: 'How long does PDD development take?', answer: 'Typically 3-6 months depending on project complexity and stakeholder engagement requirements.' },
      { question: 'Which standards do you support?', answer: 'We develop PDDs for Verra VCS, Gold Standard, Plan Vivo, and other ICROA-endorsed standards.' },
    ],
  },
  {
    slug: 'permitting-compliance',
    title: 'Permitting & Compliance',
    description: 'Up-to-the-minute permitting and compliance, navigating the regulatory landscape with expert precision and local knowledge.',
    icon: Shield,
    capabilities: [
      'Environmental permits & licences',
      'Regulatory gap analysis',
      'Government liaison & negotiation',
      'ESIA compliance coordination',
      'Legal framework advisory',
    ],
    process: ['Regulatory Mapping', 'Gap Analysis', 'Application Preparation', 'Approval & Compliance'],
    deliverables: ['Permit Applications', 'Compliance Matrix', 'Regulatory Roadmap', 'ESIA Report'],
    faqs: [
      { question: 'Do permitting requirements vary by country?', answer: 'Yes. Our team has permitting experience across 20+ jurisdictions, particularly in Sub-Saharan Africa.' },
      { question: 'Can you handle multi-country projects?', answer: 'Absolutely. We coordinate regulatory compliance across borders for transboundary carbon projects.' },
    ],
  },
  {
    slug: 'implementation',
    title: 'Implementation',
    description: 'Project management on time, in full. Minimising implementation risk through structured, proven methodologies.',
    icon: Hammer,
    capabilities: [
      'Project management & scheduling',
      'Community mobilisation',
      'Supply chain coordination',
      'Quality assurance & safety',
      'Progress reporting & dashboards',
    ],
    process: ['Mobilisation', 'Site Preparation', 'Execution', 'Handover & Commissioning'],
    deliverables: ['Implementation Plan', 'Progress Reports', 'Quality Records', 'Commissioning Certificate'],
    faqs: [
      { question: 'Do you manage the entire implementation?', answer: 'We can manage end-to-end or embed within your existing project team, depending on your needs.' },
      { question: 'What geographies do you cover?', answer: 'We operate primarily across Sub-Saharan Africa, with growing capabilities in Southeast Asia and Latin America.' },
    ],
  },
  {
    slug: 'monitoring-verification',
    title: 'Monitoring & Verification',
    description: 'Continuous post-implementation monitoring ensuring your carbon project data meets the highest standards for credit issuance.',
    icon: BarChart3,
    capabilities: [
      'Remote sensing & GIS analysis',
      'Field measurement protocols',
      'Data quality assurance',
      'Real-time monitoring dashboards',
      'VVB coordination & audit support',
    ],
    process: ['Methodology Selection', 'Monitoring Design', 'Data Collection', 'Reporting & Verification'],
    deliverables: ['Monitoring Report', 'Data Quality Plan', 'Verification Dossier', 'Performance Dashboard'],
    faqs: [
      { question: 'What monitoring technologies do you use?', answer: 'We combine satellite imagery, LiDAR, field sampling, and IoT sensors for comprehensive MRV.' },
      { question: 'How frequently is monitoring conducted?', answer: 'Typically annually with continuous remote sensing, determined by the methodology.' },
    ],
  },
  {
    slug: 'carbon-credit-trading',
    title: 'Carbon Credit Trading',
    description: 'Maximised credit trading revenue through strategic advisory on portfolio management, market timing, and buyer engagement.',
    icon: TrendingUp,
    capabilities: [
      'Portfolio strategy & optimisation',
      'Market analysis & price forecasting',
      'Buyer matchmaking & negotiation',
      'Forward contract structuring',
      'Registry management',
    ],
    process: ['Market Assessment', 'Strategy Design', 'Buyer Engagement', 'Transaction & Settlement'],
    deliverables: ['Market Analysis Report', 'Portfolio Strategy', 'Transaction Documentation', 'Registry Transfers'],
    faqs: [
      { question: 'Do you trade on behalf of clients?', answer: 'We provide advisory services and facilitate introductions to verified buyers. We do not take principal positions.' },
      { question: 'Which registries do you support?', answer: 'We work across Verra, Gold Standard, Plan Vivo, and national registries.' },
    ],
  },
  {
    slug: 'capacity-building',
    title: 'Capacity Building & Handover',
    description: 'Building your capacity to tackle climate change effectively. Knowledge transfer and training for long-term project sustainability.',
    icon: GraduationCap,
    capabilities: [
      'Technical training programmes',
      'Local team development',
      'Standard operating procedures',
      'Knowledge management systems',
      'Ongoing mentorship',
    ],
    process: ['Needs Assessment', 'Curriculum Design', 'Training Delivery', 'Assessment & Handover'],
    deliverables: ['Training Materials', 'SOPs & Manuals', 'Competency Assessments', 'Handover Documentation'],
    faqs: [
      { question: 'Why is capacity building important?', answer: 'Long-term project sustainability depends on local ownership. We ensure communities and local teams can maintain operations independently.' },
      { question: 'Do you offer ongoing support after handover?', answer: 'Yes, we provide tiered support packages including remote mentoring and annual refresher training.' },
    ],
  },
  {
    slug: 'adaptive-design',
    title: 'Adaptive Project Design',
    description: 'We undertake projects not because they\u2019re easy, but precisely because they\u2019re challenging. Iterative development responding to field data and evolving methodologies.',
    icon: RefreshCw,
    capabilities: [
      'Iterative design cycles',
      'Community co-design workshops',
      'Methodology adaptation',
      'Performance feedback loops',
      'Scale-up planning',
    ],
    process: ['Pilot Design', 'Field Testing', 'Data Analysis', 'Design Iteration & Scale'],
    deliverables: ['Adaptive Design Report', 'Pilot Results', 'Lessons Learned', 'Scale-Up Roadmap'],
    faqs: [
      { question: 'How does adaptive design differ from standard project design?', answer: 'Adaptive design incorporates structured learning cycles, allowing the project to evolve based on real-world performance data before full-scale commitment.' },
      { question: 'Is adaptive design more expensive?', answer: 'Initial costs may be marginally higher, but it significantly reduces the risk of large-scale project failure and typically delivers better long-term ROI.' },
    ],
  },
]
